import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { TableField, PageData } from "../../types";
import { rawApi, api } from "@/lib";
import Store from "../../index";
import {
  TruckZip3OverridesListItem,
  GetTruckZip3OverridesRequest,
  TruckZip3Overrides
} from "@/models/api/truckZip3Overrides/getTruckZip3Overrides";
import {
  CreateTruckZip3OverridesRequest,
  CreateTruckZip3OverridesResponse
} from "@/models/api/truckZip3Overrides/createTruckZip3Overrides";
import {
  UpdateTruckZip3OverridesResponse,
  UpdateTruckZip3OverridesRequest
} from "@/models/api/truckZip3Overrides/updateTruckZip3Overrides";
import {
  DeleteTruckZip3OverridesRequest,
  DeleteTruckZip3OverridesResponse
} from "@/models/api/truckZip3Overrides/deleteTruckZip3Overrides";
import { AxiosResponse } from "axios";

const name = "truckZip3Overrides";

if (Store.state[name]) {
  Store.unregisterModule(name);
}

@Module({ name: name, dynamic: true, store: Store, namespaced: true })
export default class TruckZip3OverridesModule extends VuexModule {
  public currentTruckZip3Overrides?: TruckZip3Overrides = null;
  public data?: Array<TruckZip3OverridesListItem> = [];
  public loading: boolean = false;
  public error?: boolean = null;
  public hideManifestedTruck: boolean = false;
  public showOnlyScheduledTrucks: boolean = false;
  public showOnlyIncorrectTruck: boolean = false;
  public errorMessage: string = null;
  public truckZip3OverridesFields: Array<TableField> = [
    { key: "facilityCode", sortable: false, label: "Facility", hidden: false },
    { key: "zipCode", label: "Zip Code", hidden: false },
    { key: "overrideZip3", label: "Override Zip3", hidden: false },
    {
      key: "actions",
      label: ""
    }
  ];

  public pageData: PageData = {
    totalPages: null,
    totalItems: null,
    currentPage: 1,
    pageSize: 30
  };

  @Action
  async getTruckZip3OverridesList(params: GetTruckZip3OverridesRequest): Promise<void> {
    const pageSize = params ? params.pageSize : null || this.pageData.pageSize || 30;
    const pageNumber = params ? params.pageNumber : null || this.pageData.currentPage + 1;

    if (pageNumber === 1) {
      this.resetTruckZip3OverridesList();
    }

    let target = `/system/zip3Overrides?page=${pageNumber}&PageSize=${pageSize}`;

    if (params.facilityFilter) {
      target += `&facilityCode=${params.facilityFilter}`;
    }

    try {
      var response = await rawApi.get<TruckZip3OverridesListItem[]>(target);
      var { totalitems, totalpages } = response.headers;
      this.setTruckZip3OverridesList(response);
      this.setCurrentPage(pageNumber);
      this.pageData.totalItems = totalitems;
      this.pageData.totalPages = totalpages;
    } catch (ex) {
      throw ex;
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async loadTruckZip3Overrides(TruckZip3OverridesId: number): Promise<void> {
    this.setcurrentTruckZip3Overrides(await this.getTruckZip3Overrides(TruckZip3OverridesId));
  }

  @Action
  async reloadTruckZip3Overrides(): Promise<void> {
    if (this.currentTruckZip3Overrides)
      this.setcurrentTruckZip3Overrides(
        await this.getTruckZip3Overrides(this.currentTruckZip3Overrides.id)
      );
  }

  @Action
  async getTruckZip3Overrides(truckId: number): Promise<TruckZip3Overrides> {
    const response = await rawApi.get<TruckZip3Overrides>(`/system/zip3Overrides/${truckId}`);

    return response.data;
  }

  @Action
  async createTruckZip3Overrides(
    request: CreateTruckZip3OverridesRequest
  ): Promise<CreateTruckZip3OverridesResponse> {
    const response = await rawApi.post<CreateTruckZip3OverridesResponse>(
      `/system/zip3Overrides`,
      request
    );

    return response.data;
  }

  @Action
  async deleteTruckZip3Overrides(
    request: DeleteTruckZip3OverridesRequest
  ): Promise<DeleteTruckZip3OverridesResponse> {
    const response = await rawApi.delete(`/system/zip3Overrides/${request.truckZip3OverridesId}`, {
      data: request
    });

    return response.data;
  }

  @Action
  async updateTruckZip3Overrides(
    request: UpdateTruckZip3OverridesRequest
  ): Promise<UpdateTruckZip3OverridesResponse> {
    const response = await rawApi.put<UpdateTruckZip3OverridesResponse>(
      `/system/zip3Overrides/${request.id}`,
      request
    );
    return response.data;
  }

  @Mutation
  setcurrentTruckZip3Overrides(truck: TruckZip3Overrides) {
    this.currentTruckZip3Overrides = truck;
  }

  @Mutation
  setTruckZip3OverridesList(response: AxiosResponse<TruckZip3OverridesListItem[]>) {
    this.pageData.totalItems = response.headers.totalitems;
    this.pageData.totalPages = response.headers.totalpages;
    this.data = response.data;
  }

  @Mutation
  resetTruckZip3OverridesList() {
    this.pageData.totalItems = 0;
    this.pageData.totalPages = 0;
    this.data = [];
  }

  @Mutation
  setLoading(isLoading: boolean) {
    this.loading = isLoading;
  }

  @Mutation
  setCurrentPage(number: number) {
    this.pageData.currentPage = number;
  }

  @Mutation
  setTruckZip3OverrideFacilityCode(facilityCode: string) {
    this.currentTruckZip3Overrides.facilityCode = facilityCode;
  }

  @Mutation
  setTruckZip3Code(zip3Code) {
    this.currentTruckZip3Overrides.zipCode = zip3Code;
  }

  @Mutation
  setTruckZip3OverrideCode(zip3OverrideCode) {
    this.currentTruckZip3Overrides.overrideZip3 = zip3OverrideCode;
  }

  get getSelectedTruckZip3Overridess(): Array<TruckZip3OverridesListItem> {
    if (!this.data) return [];

    return this.data.filter(x => x.selected);
  }

  get getSelectedTruckZip3OverridesCount(): number {
    return this.getSelectedTruckZip3Overridess.length;
  }
}
